// R1A — règles d'agrégation du mode « Résumer » ET filtrage unités/conducteurs.
// Logique PURE (aucune dépendance React) pour être testable de façon déterministe.

// Sémantique d'agrégation EXPLICITE par clé de colonne. Une clé ABSENTE = pas d'agrégat
// métier défini -> on affiche "—" (jamais une somme arbitraire). Les mesures instantanées
// CAN (rpm, fuel_pct, coolant_temp, can_speed, throttle, total_mileage_km, fuel_l...)
// sont volontairement absentes : sommer/mixer n'a pas de sens (sera revu avec R4 carburant).
export const SUMMARY_RULES = {
  // Durées / distances / compteurs d'événements cumulables -> somme
  distance_km: "sum", moving_min: "sum", idle_min: "sum", stop_min: "sum",
  speeding_events: "sum", count: "sum", points: "sum", events: "sum",
  harsh_brakes: "sum", harsh_accels: "sum", cornerings: "sum", speedings: "sum",
  km: "sum", dtc_count: "sum",
  // Vitesse max de la flotte -> max ; score éco -> moyenne
  max_speed: "max",
  score: "avg",
};

const RESUME_LABEL = (n) => `Résumé — ${n} ligne(s)`;

// Produit UNE ligne consolidée selon SUMMARY_RULES ; renvoie [] si aucune donnée.
export function summarizeRows(rows, enabledCols) {
  if (!rows || !rows.length) return [];
  const row = { vehicle_id: "__resume__", speed_unit: rows[0].speed_unit };
  let labelPlaced = false;
  enabledCols.forEach((c) => {
    const rule = c.numeric ? SUMMARY_RULES[c.key] : undefined;
    if (!rule) {
      // Première colonne sans règle -> porte le libellé "Résumé — N" ; les autres -> "—".
      row[c.key] = labelPlaced ? "—" : RESUME_LABEL(rows.length);
      labelPlaced = true;
      return;
    }
    const nums = rows.map((r) => Number(r[c.key]) || 0);
    if (rule === "max") row[c.key] = Math.max(...nums);
    else if (rule === "avg") row[c.key] = Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
    else row[c.key] = Math.round(nums.reduce((a, b) => a + b, 0) * 10) / 10;
  });
  if (!labelPlaced && enabledCols[0]) row[enabledCols[0].key] = RESUME_LABEL(rows.length);
  return [row];
}

// R4 — labels FR + résumé énergie (unités mixtes L/kWh JAMAIS fusionnées ; prix jamais sommé).
export const ENERGY_LABEL = { diesel: "Diesel", essence: "Essence", hybride: "Hybride", electric: "Électrique", autre: "Autre" };
export const SOURCE_LABEL = { CAN: "CAN", "ESTIMÉ": "Estimé", INCONNU: "Inconnu" };

export function summarizeEnergyRows(rows) {
  if (!rows || !rows.length) return [];
  const sum = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const exploit = rows.filter((r) => r.consumption_value !== null && r.consumption_value !== undefined);
  const units = new Set(exploit.map((r) => r.consumption_unit));
  let consumption_value = "—", consumption_unit = "";        // mélange L+kWh -> — (jamais additionné)
  if (units.size === 1) {
    consumption_unit = [...units][0];
    consumption_value = Math.round(exploit.reduce((a, r) => a + (Number(r.consumption_value) || 0), 0) * 100) / 100;
  }
  return [{
    vehicle_id: "__resume__", vehicle: `Résumé — ${rows.length} ligne(s)`, plate: "—", energy_type: "—",
    distance_km: Math.round(sum("distance_km") * 10) / 10,
    consumption_value, consumption_unit, source: "—",
    price: "—", price_unit: "", cost_xpf: Math.round(sum("cost_xpf")), note: "",  // coût = somme des coûts connus
  }];
}
export function csvSafe(value) {
  if (typeof value === "number") return value;  // nombre réel (ex. -5) : inchangé, jamais texté
  const s = value === null || value === undefined ? "" : String(value);
  const t = s.replace(/^\s+/, "");
  return t && "=+-@".includes(t[0]) ? "'" + s : s;
}

// R1C — échappement HTML pour l'export HTML généré côté navigateur (distinct de csvSafe).
export function htmlEscape(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

// Sélections effectives : ignore les conducteurs si le rapport ne les supporte pas.
export function effectiveUnitSelection(unitVals, vehIndex, supportsDrivers) {
  const eff = (unitVals || []).filter((v) => supportsDrivers || !String(v).startsWith("drv:"));
  const plates = eff.filter((v) => !String(v).startsWith("drv:"));
  const drivers = eff.filter((v) => String(v).startsWith("drv:")).map((v) => v.slice(4));
  const names = plates.map((p) => (vehIndex || {})[p]).filter(Boolean);
  return { eff, plates, drivers, names };
}

// Filtre les lignes selon la sélection. Ne JAMAIS interpréter un "drv:" comme une plaque.
// Retourne toutes les lignes si aucune sélection effective.
export function filterRowsByUnits(rows, unitVals, vehIndex, supportsDrivers) {
  const { eff, plates, drivers, names } = effectiveUnitSelection(unitVals, vehIndex, supportsDrivers);
  if (!eff.length) return rows;
  return rows.filter((r) =>
    (r.plate && plates.includes(r.plate)) ||                          // activité / can / alertes
    (r.vehicle_name && names.includes(r.vehicle_name)) ||             // éco attribuée à un véhicule
    (r.attributed_to === "driver" && drivers.includes(r.name)) ||     // éco attribuée à un conducteur
    (r.vehicle && names.includes(r.vehicle))                          // repli (nom véhicule)
  );
}
